import React, {useState} from 'react';
import {Button, ButtonProps, Tooltip, Typography} from '@mui/material';
import {Error, Send, ReportProblem, ReportProblemOutlined} from '@mui/icons-material';
import {Box} from '@mui/system';
import {useInvitationActions} from './hooks/useInvitationActions';
import {ReviewInvitationSummaryFragment} from '../../schema-types/ReviewInvitationSummaryFragment';
import {useRTQ} from '../../hooks/useRTQ';
import TickIcon from '../../theme/styled-components/TickIcon';
import {themeColors} from '../../theme/Theme';
import {ContactATRECQStatusEnum} from '../../schema-types/globalTypes';
import {ConfirmationModal} from '../../components/Common/ConfirmationModal';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';
import {atrEcqStatusColors} from './constants/statusColors';
import {ConfirmInvitationModal} from './components/ConfirmInvitationModal';

/**
 * - If there is a missing email address for single client, disable ‘To Invite’, RTQ and ECQ buttons
 * and display that ‘Email missing’.
 * - If there are 2 clients associated with the account and one doesn’t have an email address,
 * enable ‘To Invite’, RTQ and ECQ buttons and still display that ‘Email missing’.
 * - If there are 2 clients associated with the account and both have email address,
 * enable ‘To Invite’, RTQ and ECQ buttons and DO NOT display that ‘Email missing’.
 * - If there are 2 clients associated with the account and one or both do not have an unique address:
 *   - enable ‘To Invite’, RTQ and ECQ buttons
 *   - display ‘No unique email’ icon.
 *   - allow to click on RTQ button, but then display error message that email needs to be unique to
 * send RTQ (as per the current OneView logic).
 * @param review
 * @returns
 */
export const getButtonDisabledState = (review: ReviewInvitationSummaryFragment) => {
  const clientCount = review.contacts?.length ?? 0;
  const noEmailCount = review.contacts?.filter((x) => !x.email).length ?? 0;
  const noUniqueEmailCount =
    review.contacts?.filter((x) => !!x.email && !x.isEmailUnique).length ?? 0;

  // disabled if no clients or all clients do no have a valid email on file
  const disabled = clientCount === noEmailCount || !clientCount;
  const warningMessages = [];
  if (noEmailCount || !clientCount) {
    warningMessages.push('Email missing');
  }
  if (noUniqueEmailCount) {
    warningMessages.push('No unique email');
  }
  return {disabled, warningMessages};
};

export const InviteButton: React.FC<
  ButtonProps & {
    review: ReviewInvitationSummaryFragment;
    showDisabledButton: boolean;
    includeSendText: boolean;
  }
> = ({review, showDisabledButton, includeSendText, ...props}) => {
  const reviewId = review.id;
  const accountName = review.account?.name || 'Unknown Account';
  const {disabled, warningMessages} = getButtonDisabledState(review);
  const {scheduleInvitation, isScheduling, scheduleState} = useInvitationActions({
    accountName,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleInvite = () => {
    if (isScheduling) {
      return; // Prevent multiple clicks while loading
    }
    setShowConfirmModal(true);
  };

  const handleConfirmInvitation = async (selectedOwnerId: string) => {
    try {
      await scheduleInvitation(reviewId, selectedOwnerId);
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Error during invite process:', error);
      // Error handling is already done in the useInvitationActions hook
    }
  };

  const handleCloseModal = () => {
    setShowConfirmModal(false);
  };

  const isLoading = isScheduling;

  // Determine button content based on state
  const content = React.useMemo(() => {
    if (isLoading) {
      return 'Inviting...';
    }
    if (scheduleState.success) {
      return (
        <>
          <TickIcon fill={themeColors.green} />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send Invitations' : 'Invite'}</Box>
        </>
      );
    }
    if (scheduleState.error) {
      return (
        <>
          <ReportProblem sx={{color: themeColors.red}} />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send Invitations' : 'Invite'}</Box>
        </>
      );
    }
    if (disabled) {
      return (
        <>
          <Error />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send Invitations' : 'Invite'}</Box>
        </>
      );
    }
    return (
      <>
        <Send />
        <Box sx={{ml: 0.5}}>{includeSendText ? 'Send Invitations' : 'Invite'}</Box>
      </>
    );
  }, [isLoading, disabled, includeSendText, scheduleState, warningMessages]);

  const tooltipText = React.useMemo(() => {
    if (scheduleState.success) {
      return 'Invitation sent successfully!';
    }
    if (scheduleState.error) {
      return 'Failed to send invitation. Click to retry.';
    }
    if (disabled) {
      return `Cannot invite: ${warningMessages.join(',')}`;
    }
    return 'This will send invites to contacts.';
  }, [scheduleState, disabled, warningMessages]);

  if (!showDisabledButton && disabled) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <ReportProblemOutlined sx={{color: 'error.main'}} />
        <Typography variant="caption">{warningMessages.join(',')}</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center">
      <Tooltip title={tooltipText}>
        <span>
          <Button
            variant={
              scheduleState.success || scheduleState.error ? 'outlined' : 'contained'
            }
            onClick={(e) => {
              e.stopPropagation();
              handleInvite();
            }}
            disabled={isLoading || disabled}
            sx={{
              minWidth: 80,
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            {...props}
          >
            {content}
          </Button>
        </span>
      </Tooltip>
      <ConfirmInvitationModal
        formFields={[]}
        open={showConfirmModal}
        onClose={handleCloseModal}
        review={review}
        loading={isScheduling}
        submit={(values) => {
          if (values.selectedHost) {
            handleConfirmInvitation(values.selectedHost);
          }
        }}
      />
      {/* Don't delete this box component below as it keeps the buttons aligned */}
      <Box width="20px" display="flex" alignItems="center">
        {warningMessages.length > 0 && (
          <Tooltip title={warningMessages.join(',')}>
            <ReportProblemOutlined sx={{color: 'error.main', marginLeft: '0.5rem'}} />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export const SendRTQButton: React.FC<
  ButtonProps & {
    review: ReviewInvitationSummaryFragment;
    showDisabledButton: boolean;
    includeSendText: boolean;
  }
> = ({review, showDisabledButton: _showDisabledButton, includeSendText, ...props}) => {
  const reviewId = review.id;
  const accountName = review.account?.name || 'Unknown Account';
  const {disabled, warningMessages} = getButtonDisabledState(review);
  const {atrName} = useRTQ();
  const {sendQuestionnaires, isSendingQuestionnaires, questionnaireState} =
    useInvitationActions({
      accountName,
    });

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Check if RTQ should be sent (only if MISSING or OUTDATED)
  const shouldSendRTQ = React.useMemo(() => {
    return (
      review.atrStatus === ContactATRECQStatusEnum.MISSING ||
      review.atrStatus === ContactATRECQStatusEnum.OUTDATED
    );
  }, [review.atrStatus]);

  const handleSendRTQ = async () => {
    if (isSendingQuestionnaires) {
      return; // Prevent multiple clicks while loading
    }

    // If RTQ shouldn't be sent, show confirmation modal
    if (!shouldSendRTQ) {
      setShowConfirmModal(true);
      return;
    }

    await executeSendRTQ();
  };

  const executeSendRTQ = async () => {
    try {
      await sendQuestionnaires({
        variables: {
          reviewId,
          isEcqRequired: false,
          isRtqRequired: true,
          overrideRequiredChecks: true,
        },
      });
    } catch (error) {
      console.error('Error sending RTQ:', error);
      // Error handling is already done in the useInvitationActions hook
    }
  };

  const handleConfirmSend = async () => {
    setShowConfirmModal(false);
    await executeSendRTQ();
  };

  const isLoading = isSendingQuestionnaires;

  // Determine button content based on state
  const content = React.useMemo(() => {
    if (isLoading) {
      return 'Sending';
    }
    if (questionnaireState.success) {
      return (
        <>
          <TickIcon fill={themeColors.green} />
          <Box sx={{ml: 0.5}}>{includeSendText ? `Send ${atrName}s` : ''}</Box>
        </>
      );
    }
    if (questionnaireState.error) {
      return (
        <>
          <ReportProblem sx={{color: themeColors.red}} />
          <Box sx={{ml: 0.5}}>{includeSendText ? `Send ${atrName}s` : ''}</Box>
        </>
      );
    }
    if (disabled) {
      return (
        <>
          <Error />
          <Box sx={{ml: 0.5}}>{includeSendText ? `Send ${atrName}s` : ''}</Box>
        </>
      );
    }
    return (
      <>
        <Send />
        <Box sx={{ml: 0.5}}>{includeSendText ? `Send ${atrName}s` : ''}</Box>
      </>
    );
  }, [isLoading, disabled, includeSendText, atrName, questionnaireState]);

  const tooltipText = React.useMemo(() => {
    if (questionnaireState.success) {
      return `${atrName} sent successfully!`;
    }
    if (questionnaireState.error) {
      return `Failed to send ${atrName}. Click to retry.`;
    }
    if (disabled) {
      return `Cannot send ${atrName}: ${warningMessages.join(',')}`;
    }
    return `This will send an ${atrName} to all contacts for this account`;
  }, [questionnaireState, disabled, warningMessages, atrName]);
  return (
    <>
      <Tooltip title={tooltipText}>
        <span>
          <Button
            variant="text"
            aria-label="Send RTQ"
            onClick={(e) => {
              e.stopPropagation();
              handleSendRTQ();
            }}
            disabled={isLoading || disabled}
            sx={{
              opacity: isLoading ? 0.7 : shouldSendRTQ ? 1 : 0.6,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            {...props}
          >
            {content}
          </Button>
        </span>
      </Tooltip>
      <ConfirmationModal
        open={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        title={`Confirm ${atrName} Send`}
        message={
          <>
            <Typography variant="body1" sx={{mb: 2}}>
              <strong>Account:</strong> {accountName}
            </Typography>

            <Typography variant="body2">
              The {atrName} status is currently{' '}
              <ColoredChip
                value={review.atrStatus || ''}
                keepCase
                size="small"
                colorScheme={atrEcqStatusColors}
                sx={{display: 'inline-block'}}
              />{' '}
              . Are you sure you want to send a new {atrName} request?
            </Typography>
          </>
        }
        cancelText="Cancel"
        confirmText="Send Anyway"
        confirmAction={handleConfirmSend}
      />
    </>
  );
};

export const SendECQButton: React.FC<
  ButtonProps & {
    review: ReviewInvitationSummaryFragment;
    showDisabledButton: boolean;
    includeSendText: boolean;
  }
> = ({review, showDisabledButton: _showDisabledButton, includeSendText, ...props}) => {
  const reviewId = review.id;
  const accountName = review.account?.name || 'Unknown Account';
  const {disabled, warningMessages} = getButtonDisabledState(review);
  const {sendQuestionnaires, isSendingQuestionnaires, questionnaireState} =
    useInvitationActions({
      accountName,
    });

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Check if ECQ should be sent (only if MISSING or OUTDATED)
  const shouldSendECQ = React.useMemo(() => {
    return (
      review.ecqStatus === ContactATRECQStatusEnum.MISSING ||
      review.ecqStatus === ContactATRECQStatusEnum.OUTDATED
    );
  }, [review.ecqStatus]);

  const handleSendECQ = async () => {
    if (isSendingQuestionnaires) {
      return; // Prevent multiple clicks while loading
    }

    // If ECQ shouldn't be sent, show confirmation modal
    if (!shouldSendECQ) {
      setShowConfirmModal(true);
      return;
    }

    await executeSendECQ();
  };

  const executeSendECQ = async () => {
    try {
      await sendQuestionnaires({
        variables: {
          reviewId,
          isEcqRequired: true,
          isRtqRequired: false,
          overrideRequiredChecks: true,
        },
      });
    } catch (error) {
      console.error('Error sending ECQ:', error);
      // Error handling is already done in the useInvitationActions hook
    }
  };

  const handleConfirmSend = async () => {
    setShowConfirmModal(false);
    await executeSendECQ();
  };

  const isLoading = isSendingQuestionnaires;

  // Determine button content based on state
  const content = React.useMemo(() => {
    if (isLoading) {
      return 'Sending';
    }
    if (questionnaireState.success) {
      return (
        <>
          <TickIcon fill={themeColors.green} />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send ECQs' : ''}</Box>
        </>
      );
    }
    if (questionnaireState.error) {
      return (
        <>
          <ReportProblem sx={{color: themeColors.red}} />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send ECQs' : ''}</Box>
        </>
      );
    }
    if (disabled) {
      return (
        <>
          <Error />
          <Box sx={{ml: 0.5}}>{includeSendText ? 'Send ECQs' : ''}</Box>
        </>
      );
    }
    return (
      <>
        <Send />
        <Box sx={{ml: 0.5}}>{includeSendText ? 'Send ECQs' : ''}</Box>
      </>
    );
  }, [isLoading, disabled, includeSendText, questionnaireState]);

  const tooltipText = React.useMemo(() => {
    if (questionnaireState.success) {
      return 'ECQ sent successfully!';
    }
    if (questionnaireState.error) {
      return 'Failed to send ECQ. Click to retry.';
    }
    if (disabled) {
      return `Cannot send ECQ: ${warningMessages.join(',')}`;
    }
    return 'This will send an eCQ questionnaire to all contacts for this account';
  }, [questionnaireState, disabled, warningMessages]);
  return (
    <>
      <Tooltip title={tooltipText}>
        <span>
          <Button
            variant="text"
            aria-label="Send ECQ"
            onClick={(e) => {
              e.stopPropagation();
              handleSendECQ();
            }}
            disabled={isLoading || disabled}
            sx={{
              opacity: isLoading ? 0.7 : shouldSendECQ ? 1 : 0.6,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
            {...props}
          >
            {content}
          </Button>
        </span>
      </Tooltip>
      <ConfirmationModal
        open={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        title="Confirm ECQ Send"
        message={
          <>
            <Typography variant="body1" sx={{mb: 2}}>
              <strong>Account:</strong> {accountName}
            </Typography>

            <Typography variant="body2">
              The ECQ status is currently{' '}
              <ColoredChip
                value={review.ecqStatus || ''}
                keepCase
                size="small"
                colorScheme={atrEcqStatusColors}
                sx={{display: 'inline-block'}}
              />{' '}
              . Are you sure you want to send a new ECQ request?
            </Typography>
          </>
        }
        cancelText="Cancel"
        confirmText="Send Anyway"
        confirmAction={handleConfirmSend}
      />
    </>
  );
};
